import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const GenerationNotificationContext = createContext(null);
const MAX_GENERATION_NOTIFICATIONS = 10;

const createNotificationId = () =>
  `generation-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export function GenerationNotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [lastCompletedAt, setLastCompletedAt] = useState(null);

  const startGenerationTask = useCallback((task = {}) => {
    const id = task.id ?? createNotificationId();
    const now = new Date().toISOString();

    setNotifications((prev) => [
      {
        id,
        status: "pending",
        mediaType: task.mediaType ?? "image",
        prompt: task.prompt ?? "",
        route: task.route ?? "/generate/chatgpt",
        createdAt: now,
        updatedAt: now,
        unread: false,
      },
      ...prev.filter((item) => item.id !== id),
    ].slice(0, MAX_GENERATION_NOTIFICATIONS));

    return id;
  }, []);

  const completeGenerationTask = useCallback((id, result = {}) => {
    const now = new Date().toISOString();

    setNotifications((prev) => {
      const existing = prev.find((item) => item.id === id);
      const completedItem = {
        ...(existing ?? {}),
        id,
        status: "completed",
        mediaType: result.mediaType ?? existing?.mediaType ?? "image",
        prompt: result.prompt ?? existing?.prompt ?? "",
        mediaUrl: result.mediaUrl,
        conversationId: result.conversationId ?? existing?.conversationId,
        route: result.route ?? existing?.route ?? "/generate/chatgpt",
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
        unread: true,
      };

      return [
        completedItem,
        ...prev.filter((item) => item.id !== id),
      ].slice(0, MAX_GENERATION_NOTIFICATIONS);
    });
    setLastCompletedAt(Date.now());
  }, []);

  const failGenerationTask = useCallback((id, result = {}) => {
    const now = new Date().toISOString();

    setNotifications((prev) => {
      const existing = prev.find((item) => item.id === id);
      const failedItem = {
        ...(existing ?? {}),
        id,
        status: "failed",
        mediaType: result.mediaType ?? existing?.mediaType ?? "image",
        prompt: result.prompt ?? existing?.prompt ?? "",
        error: result.error ?? "Generation could not be completed.",
        route: result.route ?? existing?.route ?? "/generate/chatgpt",
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
        unread: true,
      };

      return [
        failedItem,
        ...prev.filter((item) => item.id !== id),
      ].slice(0, MAX_GENERATION_NOTIFICATIONS);
    });
    setLastCompletedAt(Date.now());
  }, []);

  const markGenerationRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, unread: false } : item,
      ),
    );
  }, []);

  const markAllGenerationRead = useCallback(() => {
    setNotifications((prev) =>
      prev.map((item) => (item.unread ? { ...item, unread: false } : item)),
    );
  }, []);

  const removeGenerationNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearGenerationNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const value = useMemo(() => {
    const unreadCount = notifications.filter((item) => item.unread).length;
    const activeCount = notifications.filter(
      (item) => item.status === "pending",
    ).length;

    return {
      notifications,
      unreadCount,
      activeCount,
      lastCompletedAt,
      startGenerationTask,
      completeGenerationTask,
      failGenerationTask,
      markGenerationRead,
      markAllGenerationRead,
      removeGenerationNotification,
      clearGenerationNotifications,
    };
  }, [
    notifications,
    lastCompletedAt,
    startGenerationTask,
    completeGenerationTask,
    failGenerationTask,
    markGenerationRead,
    markAllGenerationRead,
    removeGenerationNotification,
    clearGenerationNotifications,
  ]);

  return (
    <GenerationNotificationContext.Provider value={value}>
      {children}
    </GenerationNotificationContext.Provider>
  );
}

export function useGenerationNotifications() {
  const context = useContext(GenerationNotificationContext);
  if (!context) {
    throw new Error(
      "useGenerationNotifications must be used within GenerationNotificationProvider.",
    );
  }
  return context;
}
